import React from 'react';
import {
  Box,
  Button,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import {
  REPORT_COLLECTIONS,
  SERVER_FILTER_OPERATORS,
  METRIC_TYPES,
  getCollectionMeta,
} from '../../lib/reports/reportQueryConfig';

function createFilterRow(fields) {
  return {
    id: `f_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`,
    field: fields[0]?.id || 'status',
    operator: '==',
    value: '',
  };
}

/**
 * Left parameter sidebar — Metabase-style query configurator.
 */
export default function ReportQuerySidebar({
  queryConfig,
  onChange,
  onRunQuery,
  loading,
  collapsed = false,
}) {
  const meta = getCollectionMeta(queryConfig.collectionName);

  const patch = (partial) => onChange({ ...queryConfig, ...partial });

  const setCollection = (collectionName) => {
    const nextMeta = getCollectionMeta(collectionName);
    patch({
      collectionName,
      dimensions: nextMeta.dimensions[0] ? [nextMeta.dimensions[0].id] : [],
      filters: [],
      metrics: [{ type: 'count', field: '*' }],
    });
  };

  const updateFilter = (id, partial) => {
    patch({
      filters: queryConfig.filters.map((f) => (f.id === id ? { ...f, ...partial } : f)),
    });
  };

  const metric = queryConfig.metrics[0] || { type: 'count', field: '*' };
  const showMetricField = metric.type !== 'count' && meta.numericFields.length > 0;

  return (
    <Box
      sx={{
        width: collapsed ? 0 : { xs: '100%', md: '30%' },
        minWidth: collapsed ? 0 : { md: 280 },
        maxWidth: collapsed ? 0 : { md: 400 },
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        borderRight: collapsed ? 0 : 1,
        borderColor: 'divider',
        bgcolor: 'background.paper',
        opacity: collapsed ? 0 : 1,
        pointerEvents: collapsed ? 'none' : 'auto',
        transition:
          'width 0.25s ease, min-width 0.25s ease, max-width 0.25s ease, opacity 0.2s ease, border-width 0.25s ease',
      }}
      aria-hidden={collapsed}
    >
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" fontWeight={700}>
          Query
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Configure parameters, then run against your tenant data.
        </Typography>
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', px: 2, py: 2 }}>
        <Stack spacing={2}>
          <FormControl fullWidth size="small">
            <InputLabel>Data source</InputLabel>
            <Select
              label="Data source"
              value={queryConfig.collectionName}
              onChange={(e) => setCollection(e.target.value)}
            >
              {REPORT_COLLECTIONS.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box>
            <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              TIME RANGE
            </Typography>
            <Stack direction="row" spacing={1}>
              <TextField
                label="Start"
                type="date"
                size="small"
                fullWidth
                value={queryConfig.timeRange?.start || ''}
                onChange={(e) =>
                  patch({ timeRange: { ...queryConfig.timeRange, start: e.target.value } })
                }
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="End"
                type="date"
                size="small"
                fullWidth
                value={queryConfig.timeRange?.end || ''}
                onChange={(e) =>
                  patch({ timeRange: { ...queryConfig.timeRange, end: e.target.value } })
                }
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          </Box>

          <Divider />

          <Box>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary">
                FILTERS
              </Typography>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={() =>
                  patch({ filters: [...queryConfig.filters, createFilterRow(meta.filterFields)] })
                }
                sx={{ textTransform: 'none' }}
              >
                Add
              </Button>
            </Stack>
            {queryConfig.filters.length === 0 && (
              <Typography variant="caption" color="text.disabled">
                No filters applied.
              </Typography>
            )}
            <Stack spacing={1}>
              {queryConfig.filters.map((f) => (
                <Stack key={f.id} direction="row" spacing={0.5} alignItems="flex-start">
                  <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                    <InputLabel>Field</InputLabel>
                    <Select
                      label="Field"
                      value={f.field}
                      onChange={(e) => updateFilter(f.id, { field: e.target.value })}
                    >
                      {meta.filterFields.map((fld) => (
                        <MenuItem key={fld.id} value={fld.id}>
                          {fld.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
                    <InputLabel>Operator</InputLabel>
                    <Select
                      label="Operator"
                      value={f.operator}
                      onChange={(e) => updateFilter(f.id, { operator: e.target.value })}
                    >
                      {SERVER_FILTER_OPERATORS.map((op) => (
                        <MenuItem key={op.id} value={op.id}>
                          {op.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    label="Value"
                    value={f.value}
                    onChange={(e) => updateFilter(f.id, { value: e.target.value })}
                    sx={{ flex: 1.2, minWidth: 0 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => patch({ filters: queryConfig.filters.filter((x) => x.id !== f.id) })}
                    aria-label="Remove filter"
                    sx={{ mt: 0.5 }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
              ))}
            </Stack>
          </Box>

          <Divider />

          <FormControl fullWidth size="small">
            <InputLabel>Dimensions (group by)</InputLabel>
            <Select
              multiple
              label="Dimensions (group by)"
              value={queryConfig.dimensions}
              onChange={(e) => {
                const v = typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value;
                patch({ dimensions: v });
              }}
              renderValue={(selected) => selected.join(', ')}
            >
              {meta.dimensions.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Stack direction="row" spacing={1}>
            <FormControl size="small" sx={{ flex: 1 }}>
              <InputLabel>Metric</InputLabel>
              <Select
                label="Metric"
                value={metric.type}
                onChange={(e) => {
                  const type = e.target.value;
                  const field = type === 'count' ? '*' : meta.numericFields[0]?.id || '*';
                  patch({ metrics: [{ type, field }] });
                }}
              >
                {METRIC_TYPES.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {showMetricField && (
              <FormControl size="small" sx={{ flex: 1 }}>
                <InputLabel>Field</InputLabel>
                <Select
                  label="Field"
                  value={metric.field}
                  onChange={(e) => patch({ metrics: [{ ...metric, field: e.target.value }] })}
                >
                  {meta.numericFields.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Stack>
        </Stack>
      </Box>

      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Button
          variant="contained"
          fullWidth
          size="large"
          startIcon={<PlayArrowIcon />}
          onClick={onRunQuery}
          disabled={loading}
          sx={{ fontWeight: 700 }}
        >
          {loading ? 'Running…' : 'Run query'}
        </Button>
      </Box>
    </Box>
  );
}
